import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FileSignature, ChevronLeft, ChevronRight, Eye, Trash2, FileText, Settings, Eraser, Printer, Download, Share2, X } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { conventionPages, generateSignedPdf, DEFAULT_PAGES } from "../lib/convenzione";
import { PdfBlobViewer } from "./PdfBlobViewer";

const CATEGORIA = "Convenzione di ospitalità firmata";

const SignaturePad = ({ label, sigRef, testid }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const cv = canvasRef.current; const ctx = cv.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    sigRef.current = { canvas: cv, isEmpty: true };
  }, [sigRef]);
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (canvasRef.current.width / r.width), y: (t.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); sigRef.current.isEmpty = false; };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clear = () => { const cv = canvasRef.current; cv.getContext("2d").clearRect(0, 0, cv.width, cv.height); sigRef.current.isEmpty = true; };
  return (
    <div className="flex-1 min-w-[240px]">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-bold">{label}</Label>
        <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid={`${testid}-clear`}><Eraser size={13} /> Pulisci</button>
      </div>
      <canvas ref={canvasRef} width={520} height={180} data-testid={testid}
        className="w-full h-[150px] border-2 border-dashed rounded-lg bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <p className="text-[11px] text-muted-foreground mt-1">Firma con il dito o la pennina</p>
    </div>
  );
};

export default function ConvenzioneFirma({ ospite, structName, initialRetta }) {
  const qc = useQueryClient();

  const { user, companyLogo } = useAuth();
  const isMgr = ["super_admin", "admin", "responsabile"].includes(user?.role);
  const autonomo = ospite.firmatario_tipo === "ospite";
  const firmLabel = ospite.firmatario_tipo === "amministratore" ? "Amministratore di sostegno" : "Familiare / Parente / avente titolo";
  const [settings, setSettings] = useState(null);
  const [docs, setDocs] = useState([]);
  const [retta, setRetta] = useState(initialRetta || "");
  const [flow, setFlow] = useState(false);
  const [page, setPage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [emailDest, setEmailDest] = useState("");
  const [setOpen, setSetOpen] = useState(false);
  const [setForm, setSetForm] = useState({});
  const [viewer, setViewer] = useState(null); // { url, blob, name, title, print }
  const [htmlPreview, setHtmlPreview] = useState(false);
  const sigO = useRef(null); const sigF = useRef(null);
  useEffect(() => { if (viewer?.print) { const t = setTimeout(printViewer, 300); return () => clearTimeout(t); } /* eslint-disable-next-line */ }, [viewer]);

  const loadSettings = async () => { const { data } = await api.get("/impostazioni/convenzione"); setSettings(data); if (!retta) setRetta(data.retta_default || ""); };
  const loadDocs = async () => { const { data } = await api.get("/documenti"); setDocs(data.filter((d) => d.owner_type === "ospite" && d.owner_id === ospite.id && d.categoria === CATEGORIA)); };
  useEffect(() => { loadSettings(); loadDocs(); /* eslint-disable-next-line */ }, [ospite.id]);

  const ctx = () => ({
    societa: settings?.societa_nome || "", struttura: structName || "",
    ospite: `${ospite.nome} ${ospite.cognome}`,
    natoA: ospite.data_nascita ? fmtDate(ospite.data_nascita) : "",
    cf: ospite.codice_fiscale || "",
    familiare: ospite.familiare_referente || "",
    dataIngresso: ospite.data_ingresso ? fmtDate(ospite.data_ingresso) : "",
    retta: retta || "________",
    referenteTel: settings?.telefono || "", referenteEmail: settings?.email || "",
    firmatarioLabel: autonomo ? "" : `${firmLabel}${ospite.familiare_referente ? ": " + ospite.familiare_referente : ""}`,
    soloOspite: autonomo,
    luogoData: new Date().toLocaleDateString("it-IT"),
    firmatoIl: new Date().toLocaleString("it-IT"),
  });

  const startFlow = () => { setPage(0); setEmailDest(ospite.email || ""); setFlow(true); };
  const pages = conventionPages(ctx(), settings?.testo_pagine);
  const isLast = page === pages.length - 1;

  // Visualizzatore PDF integrato (funziona su mobile/tablet dove le nuove schede/popup sono bloccate)
  const openViewer = (blob, name, title, doPrint = false) => {
    const url = URL.createObjectURL(new Blob([blob], { type: blob.type || "application/pdf" }));
    setViewer({ url, blob, name, title, print: doPrint });
  };
  const closeViewer = () => setViewer((v) => { if (v?.url) setTimeout(() => URL.revokeObjectURL(v.url), 300); return null; });
  const printViewer = () => {
    if (!viewer?.url) return;
    try {
      const f = document.createElement("iframe");
      f.style.position = "fixed"; f.style.right = "0"; f.style.bottom = "0"; f.style.width = "0"; f.style.height = "0"; f.style.border = "0";
      f.src = viewer.url;
      f.onload = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch (e) { toast.info("Usa Scarica per stampare."); } setTimeout(() => { try { f.remove(); } catch (e) {} }, 60000); };
      document.body.appendChild(f);
    } catch (e) { toast.info("Usa il pulsante Scarica per stampare il documento."); }
  };
  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(new Blob([blob], { type: blob.type || "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const shareBlob = async (blob, name, title) => {
    const file = new File([blob], name, { type: "application/pdf" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: title });
        return;
      }
    } catch (e) { if (e && e.name === "AbortError") return; }
    downloadBlob(blob, name);
    toast.info("Condivisione diretta non disponibile: PDF scaricato, puoi allegarlo su WhatsApp.");
  };
  const convFileName = `Convenzione_${[ospite.cognome, ospite.nome].filter(Boolean).join("_") || "ospite"}.pdf`.replace(/\s+/g, "_");
  const convTitle = `Convenzione di ospitalità — ${ospite.nome} ${ospite.cognome}`;
  const genUnsigned = () => generateSignedPdf(ctx(), null, null, settings?.testo_pagine, companyLogo);
  const printUnsigned = async () => {
    try { openViewer(await genUnsigned(), convFileName, convTitle, true); }
    catch { toast.error("Errore nella stampa"); }
  };
  const previewUnsigned = () => setHtmlPreview(true);
  const downloadUnsigned = async () => {
    try { downloadBlob(await genUnsigned(), convFileName); toast.success("PDF scaricato"); }
    catch { toast.error("Errore download"); }
  };
  const shareUnsigned = async () => {
    try { await shareBlob(await genUnsigned(), convFileName, convTitle); }
    catch { toast.error("Errore condivisione"); }
  };
  const printSigned = async (d) => {
    try { const res = await api.get(`/documenti/${d.id}/download`, { responseType: "blob" }); openViewer(res.data, d.original_filename || convFileName, convTitle, true); }
    catch { toast.error("Errore nella stampa"); }
  };

  const finalize = async () => {
    if (sigO.current?.isEmpty) { toast.error("Manca la firma dell'ospite"); return; }
    if (!autonomo && sigF.current?.isEmpty) { toast.error(`Manca la firma di: ${firmLabel}`); return; }
    setSaving(true);
    try {
      const c = ctx();
      const famSig = (!autonomo && sigF.current && !sigF.current.isEmpty) ? sigF.current.canvas.toDataURL("image/png") : null;
      const blob = await generateSignedPdf(c, sigO.current.canvas.toDataURL("image/png"), famSig, settings?.testo_pagine, companyLogo);
      const stamp = new Date().toLocaleString("it-IT").replace(/[/:]/g, "-");
      const fd = new FormData();
      fd.append("file", new File([blob], `Convenzione_${ospite.cognome}_${ospite.nome}_${stamp}.pdf`, { type: "application/pdf" }));
      fd.append("owner_type", "ospite");
      fd.append("owner_id", ospite.id);
      fd.append("owner_nome", `${ospite.nome} ${ospite.cognome}`);
      if (ospite.structure_id) fd.append("structure_id", ospite.structure_id);
      fd.append("categoria", CATEGORIA);
      fd.append("titolo", `Convenzione di ospitalità firmata — ${new Date().toLocaleString("it-IT")}`);
      fd.append("note", `Retta mensile: € ${retta || "—"}`);
      await api.post("/documenti/upload", fd);
      toast.success("Convenzione firmata e salvata nella scheda");
      const list = await api.get("/documenti");
      const saved = list.data.filter((d) => d.owner_type === "ospite" && d.owner_id === ospite.id && d.categoria === CATEGORIA).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
      if (emailDest.trim() && saved) {
        try { await api.post(`/convenzione/${saved.id}/invia-email`, { email: emailDest.trim() }); toast.success(`Convenzione inviata via email a ${emailDest.trim()}`); }
        catch { toast.error("PDF salvato, ma invio email non riuscito"); }
      }
      setDocs(list.data.filter((d) => d.owner_type === "ospite" && d.owner_id === ospite.id && d.categoria === CATEGORIA));
      qc.invalidateQueries({ queryKey: ["osp-doc-obbl"] }); qc.invalidateQueries({ queryKey: ["conv-docs", ospite.id] });
      setFlow(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const view = async (d) => {
    try { const res = await api.get(`/documenti/${d.id}/download`, { responseType: "blob" }); openViewer(res.data, d.original_filename || convFileName, convTitle); }
    catch { toast.error("Errore apertura"); }
  };
  const downloadSigned = async (d) => {
    try { const res = await api.get(`/documenti/${d.id}/download`, { responseType: "blob" }); downloadBlob(res.data, d.original_filename || convFileName); toast.success("PDF scaricato"); }
    catch { toast.error("Errore download"); }
  };
  const shareSigned = async (d) => {
    try { const res = await api.get(`/documenti/${d.id}/download`, { responseType: "blob" }); await shareBlob(res.data, d.original_filename || convFileName, `Convenzione di ospitalità — ${ospite.nome} ${ospite.cognome}`); }
    catch { toast.error("Errore condivisione"); }
  };
  const remove = async (id) => { try { await api.delete(`/documenti/${id}`); toast.success("Documento eliminato"); loadDocs(); } catch { toast.error("Errore"); } };

  const openSettings = () => { setSetForm({ ...(settings || {}), testo_pagine: (settings?.testo_pagine?.length === 4 ? settings.testo_pagine : DEFAULT_PAGES) }); setSetOpen(true); };
  const saveSettings = async () => { try { await api.put("/impostazioni/convenzione", setForm); toast.success("Impostazioni salvate"); setSetOpen(false); loadSettings(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  return (
    <div className="gr-card p-5" data-testid="convenzione-panel">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-head font-bold flex items-center gap-2"><FileSignature size={18} className="text-primary" /> Convenzione di Ospitalità</h2>
          <p className="text-sm text-muted-foreground">Documento precompilato coi dati dell'ospite. Imposta la retta e avvia la firma digitale.</p>
        </div>
        {isMgr && <Button variant="outline" size="sm" onClick={openSettings} data-testid="conv-settings-btn"><Settings size={15} className="mr-1" /> Intestazione</Button>}
      </div>

      <div className="flex items-end gap-3 flex-wrap mb-5 rounded-lg border bg-secondary/30 p-3">
        <div>
          <Label className="text-xs font-semibold mb-1 block">Retta mensile (€)</Label>
          <Input type="number" value={retta} onChange={(e) => setRetta(e.target.value)} placeholder="es. 1800" className="w-40" data-testid="conv-retta" />
        </div>
        {docs.length === 0
          ? <Button onClick={startFlow} data-testid="conv-start" className="font-semibold"><FileSignature size={16} className="mr-1" /> Avvia procedura di firma</Button>
          : <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="conv-already-signed"><FileSignature size={16} /> Convenzione già firmata</span>}
        <Button variant="outline" onClick={previewUnsigned} data-testid="conv-preview"><Eye size={16} className="mr-1" /> Anteprima</Button>
        <Button variant="outline" onClick={printUnsigned} data-testid="conv-print-unsigned"><Printer size={16} className="mr-1" /> Stampa</Button>
        <Button variant="outline" onClick={downloadUnsigned} data-testid="conv-download-unsigned"><Download size={16} className="mr-1" /> Scarica</Button>
        <Button variant="outline" onClick={shareUnsigned} data-testid="conv-share-unsigned"><Share2 size={16} className="mr-1" /> Condividi</Button>
        {!settings?.societa_nome && <p className="text-xs text-amber-600">⚠️ Imposta l'intestazione (nome società) dal pulsante "Intestazione".</p>}
      </div>

      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Documenti firmati</div>
      {docs.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna convenzione firmata.</p> : (
        <div className="space-y-2" data-testid="conv-signed-list">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 rounded-lg border bg-white p-2.5" data-testid={`conv-doc-${d.id}`}>
              <FileText size={16} className="text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{d.titolo || d.original_filename}</div>
                <div className="text-[11px] text-muted-foreground">Firmato il {fmtDate(d.created_at)} {new Date(d.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}{d.note ? ` · ${d.note}` : ""}</div>
              </div>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => view(d)} data-testid={`conv-view-${d.id}`}><Eye size={15} /></Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => printSigned(d)} data-testid={`conv-print-${d.id}`}><Printer size={15} /></Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => downloadSigned(d)} data-testid={`conv-download-${d.id}`}><Download size={15} /></Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => shareSigned(d)} data-testid={`conv-share-${d.id}`}><Share2 size={15} /></Button>
              {isMgr && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => remove(d.id)} data-testid={`conv-del-${d.id}`}><Trash2 size={15} className="text-destructive" /></Button>}
            </div>
          ))}
        </div>
      )}

      {/* Signing flow */}
      <Dialog open={flow} onOpenChange={setFlow}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Convenzione di Ospitalità — Pagina {page + 1} di {pages.length}</DialogTitle>
            <DialogDescription>{(settings?.societa_nome || "Struttura")} · {ospite.nome} {ospite.cognome}</DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border bg-white p-4 text-sm whitespace-pre-wrap leading-relaxed min-h-[46vh]" data-testid={`conv-page-${page}`}>
            {pages[page]}
          </div>

          {isLast && (
            <div className="mt-3">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Firme — Luogo e data: {new Date().toLocaleDateString("it-IT")}</div>
              <div className="flex gap-4 flex-wrap">
                <SignaturePad label={`Ospite: ${ospite.nome} ${ospite.cognome}`} sigRef={sigO} testid="conv-sig-ospite" />
                {!autonomo && <SignaturePad label={`${firmLabel}${ospite.familiare_referente ? ": " + ospite.familiare_referente : ""}`} sigRef={sigF} testid="conv-sig-familiare" />}
              </div>
              {!autonomo && (
              <div className="mt-3">
                <Label className="text-xs font-semibold mb-1 block">Email {ospite.firmatario_tipo === "amministratore" ? "amministratore" : "familiare"} (invio via email con link al PDF)</Label>
                <Input type="email" value={emailDest} onChange={(e) => setEmailDest(e.target.value)} placeholder="es. familiare@email.it — lascia vuoto per non inviare" data-testid="conv-email-dest" />
              </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-row justify-between sm:justify-between">
            <Button variant="outline" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="conv-prev"><ChevronLeft size={16} className="mr-1" /> Indietro</Button>
            {!isLast
              ? <Button onClick={() => setPage((p) => Math.min(pages.length - 1, p + 1))} data-testid="conv-next">Avanti <ChevronRight size={16} className="ml-1" /></Button>
              : <Button onClick={finalize} disabled={saving} data-testid="conv-finalize" className="font-semibold">{saving ? "Salvataggio..." : "Firma e salva PDF"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer PDF integrato */}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && closeViewer()}>
        <DialogContent className="max-w-4xl w-[calc(100vw-1rem)] sm:w-full max-h-[94vh] p-0 gap-0 overflow-hidden" data-testid="conv-viewer">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-base flex items-center gap-2"><FileText size={17} className="text-primary" /> {viewer?.title || "Documento"}</DialogTitle>
            <DialogDescription className="sr-only">Anteprima del documento PDF</DialogDescription>
          </DialogHeader>
          {viewer && (
            <PdfBlobViewer blob={viewer.blob} className="max-h-[70vh]" />
          )}
          <DialogFooter className="px-4 py-3 border-t flex-row flex-wrap gap-2 sm:justify-end">
            <Button variant="outline" size="sm" onClick={printViewer} data-testid="conv-viewer-print"><Printer size={15} className="mr-1" /> Stampa</Button>
            <Button variant="outline" size="sm" onClick={() => { downloadBlob(viewer.blob, viewer.name); toast.success("PDF scaricato"); }} data-testid="conv-viewer-download"><Download size={15} className="mr-1" /> Scarica</Button>
            <Button variant="outline" size="sm" onClick={() => shareBlob(viewer.blob, viewer.name, viewer.title)} data-testid="conv-viewer-share"><Share2 size={15} className="mr-1" /> Condividi</Button>
            <Button size="sm" onClick={closeViewer} data-testid="conv-viewer-close"><X size={15} className="mr-1" /> Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anteprima HTML scrollabile — mostra TUTTE le pagine su ogni dispositivo (incluso iPad) */}
      <Dialog open={htmlPreview} onOpenChange={setHtmlPreview}>
        <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full max-h-[94vh] p-0 gap-0 overflow-hidden" data-testid="conv-html-preview">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-base flex items-center gap-2"><FileText size={17} className="text-primary" /> Anteprima convenzione — {ospite.nome} {ospite.cognome}</DialogTitle>
            <DialogDescription className="sr-only">Anteprima del testo della convenzione, tutte le pagine</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-3 py-4 bg-slate-100 space-y-4" style={{ maxHeight: "72vh" }} data-testid="conv-html-pages">
            {pages.map((txt, i) => (
              <div key={i} className="bg-white mx-auto shadow-sm rounded-sm p-6 max-w-[760px] text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap" data-testid={`conv-html-page-${i + 1}`}>
                <div className="text-[10px] font-bold uppercase tracking-wide text-primary mb-3 pb-2 border-b">{(settings?.societa_nome || "Struttura")} — Convenzione di ospitalità · Pagina {i + 1} di {pages.length}</div>
                {txt}
              </div>
            ))}
            <div className="bg-white mx-auto shadow-sm rounded-sm p-6 max-w-[760px] text-[13px] text-slate-800" data-testid="conv-html-firme">
              <div className="font-bold mb-3">Firme</div>
              <div className="grid grid-cols-2 gap-4">
                <div><div className="h-16 border rounded mb-1" /><div className="text-xs font-semibold">Ospite: {ospite.nome} {ospite.cognome}</div></div>
                {!autonomo && <div><div className="h-16 border rounded mb-1" /><div className="text-xs font-semibold">{firmLabel || "Familiare / Referente"}</div></div>}
              </div>
            </div>
          </div>
          <DialogFooter className="px-4 py-3 border-t flex-row flex-wrap gap-2 sm:justify-end">
            <Button variant="outline" size="sm" onClick={downloadUnsigned} data-testid="conv-html-download"><Download size={15} className="mr-1" /> Scarica PDF</Button>
            <Button size="sm" onClick={() => setHtmlPreview(false)} data-testid="conv-html-close"><X size={15} className="mr-1" /> Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Intestazione e testo documento</DialogTitle>
            <DialogDescription>Dati della società/struttura che appaiono nell'intestazione della convenzione.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">Nome società / gestore</Label><Input value={setForm.societa_nome || ""} onChange={(e) => setSetForm((p) => ({ ...p, societa_nome: e.target.value }))} data-testid="set-societa" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Indirizzo</Label><Input value={setForm.indirizzo || ""} onChange={(e) => setSetForm((p) => ({ ...p, indirizzo: e.target.value }))} data-testid="set-indirizzo" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">P. IVA / C.F.</Label><Input value={setForm.piva || ""} onChange={(e) => setSetForm((p) => ({ ...p, piva: e.target.value }))} data-testid="set-piva" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Telefono</Label><Input value={setForm.telefono || ""} onChange={(e) => setSetForm((p) => ({ ...p, telefono: e.target.value }))} data-testid="set-telefono" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">E-mail</Label><Input value={setForm.email || ""} onChange={(e) => setSetForm((p) => ({ ...p, email: e.target.value }))} data-testid="set-email" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Retta predefinita (€)</Label><Input type="number" value={setForm.retta_default || ""} onChange={(e) => setSetForm((p) => ({ ...p, retta_default: e.target.value }))} data-testid="set-retta" /></div>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold">Testo della convenzione (4 pagine)</Label>
                <button type="button" onClick={() => setSetForm((p) => ({ ...p, testo_pagine: [...DEFAULT_PAGES] }))} className="text-xs text-primary font-semibold" data-testid="set-testo-reset">Ripristina predefinito</button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">Puoi usare i segnaposto: {"{{ospite}} {{natoA}} {{cf}} {{familiare}} {{dataIngresso}} {{retta}} {{societa}} {{struttura}} {{referenteTel}} {{referenteEmail}}"} — verranno sostituiti automaticamente.</p>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="mb-2">
                  <Label className="text-[11px] font-semibold mb-1 block">Pagina {i + 1}</Label>
                  <Textarea rows={5} value={(setForm.testo_pagine || DEFAULT_PAGES)[i] || ""} onChange={(e) => setSetForm((p) => { const arr = [...(p.testo_pagine || DEFAULT_PAGES)]; arr[i] = e.target.value; return { ...p, testo_pagine: arr }; })} data-testid={`set-testo-${i}`} className="text-xs font-mono" />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSetOpen(false)}>Annulla</Button><Button onClick={saveSettings} data-testid="set-save" className="font-semibold">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
